import {
  convertToModelMessages,
  pruneMessages,
  type ModelMessage,
  type UIMessage,
} from 'ai';
import objectHash from 'object-hash';
import type {
  Fact,
  LLMInputBuilder,
  Renderer,
  RenderContext,
  Skill,
} from '../llm/llm-input-types';
import { applyReceiptsToToolParts } from './conversation-receipt-splice';
import { applyToolImageOutputs } from './tool-image';
import { renderIngotSchema } from './ingot-receipt';
import type { ConversationPromptOptions } from './conversation-prompt-source';
import { COCO_INGOT_QUERY_TOOL_KEY } from './coco-tools';
import type { LlmPromptTrigger } from '../llm/llm-input-types';
import { SystemMessageClass, type Message as MessageClass } from './message';
import type { WorkspaceDomainLoader } from './workspace-prefetch';

export type ConversationMessageWindowOptions = {
  /** Return true to drop the message from the prompt. */
  prune?: (message: MessageClass, index: number) => boolean;
};

/**
 * Selects which conversation messages are rendered into the LLM prompt.
 *
 * - Includes the trailing contiguous compaction block (if any) and all messages after it
 * - Excludes in-flight streaming rows
 * - Applies optional `prune` (same semantics as {@link ConversationRendererOptions.prune})
 */
export function selectMessagesForModelPrompt(
  messages: readonly MessageClass[],
  opts: ConversationMessageWindowOptions = {}
): MessageClass[] {
  const afterCompaction = messagesAfterCompaction(messages);
  let relevant = afterCompaction.filter(
    (msg) =>
      msg.status !== 'streaming' &&
      hasNonEmptyParts(msg) &&
      !SystemMessageClass.isConversationSystemStorage(msg)
  );
  if (opts.prune !== undefined) {
    relevant = relevant.filter((msg, index) => !opts.prune!(msg, index));
  }
  return relevant;
}

function hasNonEmptyParts(message: MessageClass): boolean {
  const parts = message.raw().parts;
  if (!Array.isArray(parts)) {
    return true;
  }
  return parts.length > 0;
}

/**
 * Messages from the latest compaction block onwards.
 * If multiple compaction messages were appended as a batch, keeps the full
 * trailing compaction block so multi-block summaries stay together.
 */
export function messagesAfterCompaction(
  messages: readonly MessageClass[]
): MessageClass[] {
  let lastCompactionIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.isCompactionMessage()) {
      lastCompactionIndex = i;
      break;
    }
  }

  if (lastCompactionIndex === -1) {
    return [...messages];
  }

  let startIndex = lastCompactionIndex;
  for (let i = lastCompactionIndex - 1; i >= 0; i--) {
    if (!messages[i]?.isCompactionMessage()) {
      break;
    }
    startIndex = i;
  }

  return [...messages.slice(startIndex)];
}

/**
 * Whether durable learned facts should be loaded for this prompt. Mirrors
 * {@link ChatMessageUpdatedFactsHandler} / step-chat injection gates: top-level
 * Coco chats only (workspace tool loaded), not warehouse/compaction-summary runs
 * or nested sub-agents.
 */
export function shouldInjectLearnedFactsForPrompt(params: {
  parentConversationId: string | null;
  trigger: LlmPromptTrigger | null;
  loadedToolKeys: readonly string[];
}): boolean {
  const isTopLevel =
    params.parentConversationId === null || params.parentConversationId === '';
  if (!isTopLevel) {
    return false;
  }
  if (
    params.trigger === 'warehouse' ||
    params.trigger === 'compaction-summary'
  ) {
    return false;
  }
  return params.loadedToolKeys.includes(COCO_INGOT_QUERY_TOOL_KEY);
}

/** Per-turn clock injection for the LLM prompt; omitted for warehouse graph-column runs. */
export function shouldInjectTimestampForPrompt(
  trigger: LlmPromptTrigger | null
): boolean {
  return trigger !== 'warehouse';
}

/**
 * Whether developer {@link SkillsRegistry} skills (instructions + bundled tools)
 * should be loaded for this prompt. Interactive Coco chats only — not warehouse,
 * cron, compaction-summary, Slack, or other automation triggers.
 *
 * Set `injectSkills: false` to skip all registry skills (e.g. extract-object runs).
 */
export function shouldInjectRegistrySkillsForPrompt(params: {
  trigger: LlmPromptTrigger | null;
  injectSkills?: boolean;
}): boolean {
  if (params.injectSkills === false) {
    return false;
  }
  return params.trigger === 'chat';
}

/**
 * System guidance appended for group chats so the model uses the per-message
 * `From: <name>` labels instead of treating every turn as the chat owner.
 */
export const MULTI_PARTICIPANT_SYSTEM_NOTE = [
  'This is a group conversation with more than one person.',
  'Each user message is prefixed with a line like "From: <name>" identifying who sent that message; different messages may come from different people.',
  'Track who is speaking, address participants by name where appropriate, and do not assume every message is from the same person or greet everyone as the original user.',
].join(' ');

/** Max chars for a sender display name in the `From:` label. */
export const MAX_SENDER_NAME_LENGTH = 64;

/**
 * Sanitize a participant display name before interpolating it into the prompt.
 * Display names are user-controlled, so a crafted name (embedded newlines,
 * control chars) could otherwise forge extra lines / a fake turn inside the
 * `From:` header. Collapse all whitespace to single spaces, strip control
 * chars, and cap length so the name stays a single bounded inline token. This
 * is not a complete prompt-injection defense — the name still appears in the
 * prompt — it just prevents structural breakout of the label.
 */
export function sanitizeSenderName(name: string): string {
  const collapsed = name
    // eslint-disable-next-line no-control-regex -- intentionally stripping C0/C1 controls
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return collapsed.length > MAX_SENDER_NAME_LENGTH
    ? `${collapsed.slice(0, MAX_SENDER_NAME_LENGTH - 1)}…`
    : collapsed;
}

/**
 * Prefix a user {@link UIMessage} with a `From: <name>` line so the sender is
 * visible to the model. Prepends to the first text part, or inserts a new text
 * part when the message has none (e.g. image-only turns).
 */
function labelUserMessageWithSender(
  uiMessage: UIMessage,
  senderName: string
): UIMessage {
  const parts = uiMessage.parts ?? [];
  const firstTextIndex = parts.findIndex((p) => p.type === 'text');
  const header = `From: ${senderName}`;
  if (firstTextIndex === -1) {
    return {
      ...uiMessage,
      parts: [{ type: 'text', text: header }, ...parts],
    };
  }
  const nextParts = parts.map((part, index) => {
    if (index !== firstTextIndex || part.type !== 'text') {
      return part;
    }
    return { ...part, text: `${header}\n\n${part.text}` };
  });
  return { ...uiMessage, parts: nextParts };
}

export class ConversationRenderer implements Renderer {
  constructor(private readonly inputBuilder: LLMInputBuilder) {}

  async system(_ctx: RenderContext): Promise<string | null> {
    const raw = this.inputBuilder.getSystem();
    if (raw === null || !(raw instanceof SystemMessageClass)) {
      return null;
    }
    const loader = this.inputBuilder.getConversationPromptOptions()
      ?.workspaceDomainLoader as WorkspaceDomainLoader | undefined;
    const resolved = (
      await raw.resolveToSystemString({ workspaceDomainLoader: loader })
    ).trim();
    return resolved.length > 0 ? resolved : null;
  }

  messagesCacheKey(
    messageInstances: MessageClass[],
    _ctx: RenderContext
  ): string {
    const rawOptions = this.inputBuilder.getConversationPromptOptions();
    const parts: string[] = [];
    const system = this.inputBuilder.getSystem();
    if (system !== null) {
      parts.push(system.stableCacheKey());
    }
    const relevant = selectMessagesForModelPrompt(messageInstances, {
      ...(rawOptions?.prune !== undefined && { prune: rawOptions.prune }),
    });
    parts.push(...relevant.map((m) => m.stableCacheKey()));
    return objectHash(parts);
  }

  async conversationMessages(_ctx: RenderContext): Promise<ModelMessage[]> {
    const messageInstances = this.inputBuilder.getMessages();
    const rawOptions = this.inputBuilder.getConversationPromptOptions();
    if (messageInstances.length === 0 || rawOptions === undefined) {
      throw new Error(
        'LLMInputBuilder is missing conversation messages or prompt options; use Conversation.toLlmInput() first.'
      );
    }

    const dynamicMessages = [
      ...(await this.renderDynamicModelMessages({})),
      ...this.buildTailUserMessages(),
    ];

    const options = rawOptions as ConversationPromptOptions;
    const messages = await this.assembleConversationModelMessages(
      messageInstances,
      options,
      dynamicMessages
    );

    // Last of all, after the newest tool result: the schema is rebuilt every
    // step and changes whenever a tool writes a table, so at the very end it
    // is always current where the model reads it, and a change to it leaves
    // the cached prefix before it untouched.
    const ingotSchema = this.inputBuilder.getIngotSchema();
    return ingotSchema === null
      ? messages
      : [
          ...messages,
          {
            role: 'user',
            content: [{ type: 'text', text: renderIngotSchema(ingotSchema) }],
          },
        ];
  }

  /**
   * Per-turn dynamic block: builder facts and supplemental {@link Skill}s.
   * (System text stays in conversation history; time/active context are in the
   * tail; the ingot schema goes after everything, in {@link conversationMessages}.)
   */
  async renderDynamicModelMessages(
    renderer: Pick<Renderer, 'facts' | 'skill'>
  ): Promise<ModelMessage[]> {
    const ctx = this.inputBuilder.createRenderContext();
    const messages: ModelMessage[] = [];

    const push = (msg: ModelMessage | ModelMessage[] | null | undefined) => {
      if (msg === null || msg === undefined) {
        return;
      }
      if (Array.isArray(msg)) {
        messages.push(...msg);
      } else {
        messages.push(msg);
      }
    };

    const facts = this.inputBuilder.getFacts();
    if (facts.length > 0) {
      const rendered = renderer.facts?.(Array.from(facts), ctx);
      if (rendered !== undefined && rendered !== null) {
        push(rendered);
      } else {
        push(this.renderDefaultFactsMessage(Array.from(facts)));
      }
    }

    for (const skill of this.inputBuilder.getSkills()) {
      push(this.renderSupplementalSkill(skill, renderer, ctx));
    }

    return messages;
  }

  /**
   * Default rendering for facts when the caller-supplied renderer does not
   * provide its own `facts` implementation. Groups by `Fact.type` so the model
   * sees `user`-scope facts under one header and `org`-scope facts under
   * another; any other type is emitted under a fallback "## Other facts"
   * section so unknown fact shapes still reach the model.
   */
  private renderDefaultFactsMessage(facts: Fact[]): ModelMessage | null {
    if (facts.length === 0) {
      return null;
    }
    const userFacts = facts.filter((f) => f.type === 'user');
    const orgFacts = facts.filter((f) => f.type === 'org');
    const otherFacts = facts.filter(
      (f) => f.type !== 'user' && f.type !== 'org'
    );
    const sections: string[] = [
      '# What we know about this user and org',
      '',
      'Use these durable facts to tailor your replies. They were learned from prior chats and may be outdated — defer to anything the user says now.',
    ];
    if (userFacts.length > 0) {
      sections.push('', '## About this user');
      for (const f of userFacts) {
        sections.push(`- ${f.text}`);
      }
    }
    if (orgFacts.length > 0) {
      sections.push('', '## About this organization');
      for (const f of orgFacts) {
        sections.push(`- ${f.text}`);
      }
    }
    if (otherFacts.length > 0) {
      sections.push('', '## Other facts');
      for (const f of otherFacts) {
        sections.push(`- ${f.text}`);
      }
    }
    return {
      role: 'user',
      content: [{ type: 'text', text: sections.join('\n') }],
    };
  }

  private renderSupplementalSkill(
    skill: Skill,
    renderer: Pick<Renderer, 'skill'>,
    ctx: RenderContext
  ): ModelMessage | ModelMessage[] | null | undefined {
    const rendered = renderer.skill?.(skill, ctx);
    if (rendered !== undefined && rendered !== null) {
      return rendered;
    }
    return {
      role: 'user',
      content: [
        {
          type: 'text',
          text: ctx.encode({
            skill: skill.name,
            instructions: skill.instructions,
            ...(skill.facts !== undefined && skill.facts.length > 0
              ? { facts: skill.facts }
              : {}),
          }),
        },
      ],
    };
  }

  private async assembleConversationModelMessages(
    messageInstances: MessageClass[],
    options: ConversationPromptOptions,
    dynamicMessages: ModelMessage[]
  ): Promise<ModelMessage[]> {
    const promptMessages = selectMessagesForModelPrompt(messageInstances, {
      prune: options.prune,
    });
    const loader = options.workspaceDomainLoader;
    // Splice receipts in at the MessageClass layer (before LLM conversion) so we
    // can use the message's built-in tool-part manipulation. Receipt splicing
    // only touches assistant tool-output parts, so it's independent of the
    // file/editor-state conversion toLlmAsync does on user/system parts.
    const messagesWithReceipts = applyReceiptsToToolParts(
      promptMessages,
      this.inputBuilder.getReceipts(),
      { collapseCompletedTurns: true }
    );
    const llmMessages = await Promise.all(
      messagesWithReceipts.map((msgClass) =>
        msgClass.toLlmAsync({ workspaceDomainLoader: loader })
      )
    );

    // In group chats, prefix each user turn with `From: <name>` so the model
    // can tell participants apart. `llmMessages` is 1:1 with
    // `messagesWithReceipts`, so indices line up with the source MessageClass.
    const participantContext = options.participantContext;
    const labeledLlmMessages =
      participantContext === undefined
        ? llmMessages
        : llmMessages.map((uiMessage, index) => {
            if (uiMessage.role !== 'user') {
              return uiMessage;
            }
            const senderId = messagesWithReceipts[index]?.senderActorId;
            const rawName =
              senderId !== undefined
                ? participantContext.namesByActorId[senderId]
                : undefined;
            const senderName =
              rawName !== undefined ? sanitizeSenderName(rawName) : undefined;
            if (senderName === undefined || senderName === '') {
              return uiMessage;
            }
            return labelUserMessageWithSender(uiMessage, senderName);
          });

    const modelMessages = await convertToModelMessages(labeledLlmMessages);

    const prunedModelMessages = pruneMessages({
      messages: modelMessages,
      reasoning: 'all',
      emptyMessages: 'remove',
    });

    const baseSystemMessages = prunedModelMessages.filter(
      (m) => m.role === 'system'
    );
    const nonSystemMessages = prunedModelMessages.filter(
      (m) => m.role !== 'system'
    );
    // Group chats get an extra system note explaining the `From:` labels.
    const systemMessages: ModelMessage[] =
      participantContext === undefined
        ? baseSystemMessages
        : [
            ...baseSystemMessages,
            { role: 'system', content: MULTI_PARTICIPANT_SYSTEM_NOTE },
          ];

    const assembled = ((): ModelMessage[] => {
      if (nonSystemMessages.length === 0) {
        return [...systemMessages, ...dynamicMessages];
      }

      let lastUserIndex = -1;
      for (let i = nonSystemMessages.length - 1; i >= 0; i--) {
        if (nonSystemMessages[i]!.role === 'user') {
          lastUserIndex = i;
          break;
        }
      }

      if (lastUserIndex === -1) {
        return [...systemMessages, ...nonSystemMessages, ...dynamicMessages];
      }

      return [
        ...systemMessages,
        ...nonSystemMessages.slice(0, lastUserIndex),
        ...dynamicMessages,
        ...nonSystemMessages.slice(lastUserIndex),
      ];
    })();

    // Deliver any tool-returned images as user messages. Done last, after
    // dynamic messages (facts, skills, timestamp) have been spliced in, so the
    // injected image stays adjacent to the tool result that references it.
    return applyToolImageOutputs(assembled);
  }

  private buildTailUserMessages(): ModelMessage[] {
    const tailMessages: ModelMessage[] = [];
    if (shouldInjectTimestampForPrompt(this.inputBuilder.getTrigger())) {
      tailMessages.push({
        role: 'user',
        content: [
          {
            type: 'text',
            text: `Current date and time: ${new Date().toISOString()}`,
          },
        ],
      });
    }
    return tailMessages;
  }
}
